import { useSelector } from 'react-redux';
import { PLAN_FEATURES } from '../lib/permissions';

/**
 * Returns plan-gating helpers derived from the company's current plan.
 * Reads the Super-Admin-managed dynamic config from Redux (planConfigSlice,
 * fetched once in the dashboard layout) and falls back to the static
 * PLAN_FEATURES defaults if that hasn't loaded yet — e.g. subscription-service
 * is down — so the sidebar/gating never crashes on a missing fetch.
 * hasFeature — true if the feature is included in the current plan.
 * requiresUpgrade — true if the feature is NOT in the current plan (show <UpgradePrompt>).
 * limits — { employee_limit, lens_device_limit, company_limit } for the current plan.
 */
export function usePlan() {
  const company = useSelector((state) => state.company);
  const dynamicFeatures = useSelector((state) => state.planConfig.featuresByPlan[company.plan]);
  const dynamicLimits = useSelector((state) => state.planConfig.limitsByPlan[company.plan]);

  const staticFeatures = PLAN_FEATURES[company.plan] ?? PLAN_FEATURES.BASIC;
  const features = dynamicFeatures ?? null; // object map { feature_key: boolean } once loaded
  const limits = dynamicLimits ?? {};

  const isEnabled = (feature) => {
    if (features) return !!features[feature];
    return staticFeatures.includes(feature); // fail-open fallback (array shape)
  };

  return {
    plan: company.plan,
    limits,
    hasFeature: (feature) => {
      if (company.plan === 'CUSTOM') return true;
      return isEnabled(feature);
    },
    requiresUpgrade: (feature) => {
      if (company.plan === 'CUSTOM') return false;
      return !isEnabled(feature);
    },
  };
}
